//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { BodyPrimaryAnchor } from '@components/atoms/anchor/Anchor';
import { BodyText, SectionText } from '@components/atoms/text/Text';

interface Props {
  image: React.ReactElement;
  items: string[];
  seeMoreUrl: string;
  seeMoreTrackingId: string;
  title: string;
}

export const FeatureCard = ({
  image,
  items,
  title,
  seeMoreUrl,
  seeMoreTrackingId,
}: Props) => {
  return (
    <CardContainer>
      <ImageContainer>{image}</ImageContainer>
      <TitleText>{title}</TitleText>
      <BodyText>
        <CardItemList>
          {items.map((item, index) => (
            <CardItem key={index}>{item}</CardItem>
          ))}
        </CardItemList>
      </BodyText>
      <SeeMoreAnchor trackingId={seeMoreTrackingId} href={seeMoreUrl}>
        See more
      </SeeMoreAnchor>
    </CardContainer>
  );
};

const CardContainer = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacings[3]};
  text-align: left;
`,
);

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 120px;
`;

const CardItemList = styled.ul(
  ({ theme }) => `
  list-style-position: outside;
  padding-left: ${theme.spacings[5]}
`,
);

const TitleText = styled(SectionText)`
  text-wrap: nowrap;
`;

const CardItem = styled.li`
  text-wrap: nowrap;
`;

const SeeMoreAnchor = styled(BodyPrimaryAnchor).attrs({ target: '_blank' })(
  ({ theme }) => `
  &&, &&:hover {
    color: ${theme.colors.teal2};
  }
`,
);
